import { UploadQueueManager } from "../queue";
import type {
  QueueErrorArgs,
  QueueJobArgs,
  QueueMergeOnDuplicateArgs,
  QueueProcessResult,
  QueueSuccessArgs,
} from "../queue";
import type {
  AudioUploadDiagnosticEvent,
  AudioUploadController,
  AudioUploadEnqueueArgs,
  AudioUploadErrorRef,
  AudioUploadJob,
  AudioUploadManagerCallbacks,
  AudioUploadManagerConfig,
  AudioUploadManagerHandlers,
  AudioUploadProgressClearProgressArgs,
  AudioUploadProgressSetProgressArgs,
  PreparedAudioUploadResult,
  UploadCompletionResult,
  UploadTransportResult,
} from "./types";
import { AudioUploadDiagnosticEventType, AudioUploadFailureStage } from "./types";

export class AudioUploadManager<TSourceRef> implements AudioUploadController {
  private readonly handlers: AudioUploadManagerHandlers<TSourceRef>;
  private readonly callbacks: AudioUploadManagerCallbacks | undefined;
  private readonly diagnostics: AudioUploadManagerConfig<TSourceRef>["diagnostics"];
  private readonly queue: UploadQueueManager<AudioUploadJob, UploadCompletionResult>;

  constructor({ handlers, callbacks, diagnostics }: AudioUploadManagerConfig<TSourceRef>) {
    this.handlers = handlers;
    this.callbacks = callbacks;
    this.diagnostics = diagnostics;
    this.queue = new UploadQueueManager<AudioUploadJob, UploadCompletionResult>({
      dedupeKey: this.dedupeKey,
      mergeOnDuplicate: this.mergeOnDuplicate,
      diagnostics: diagnostics?.queue,
      callbacks: {
        onSuccess: this.onQueueSuccess,
        onError: this.onQueueError,
      },
      process: this.processJob,
    });
  }

  enqueue({ job }: AudioUploadEnqueueArgs): void {
    this.queue.enqueue({ job });
  }

  resetState(): void {
    this.queue.reset();
  }

  isActive(): boolean {
    return this.queue.isActive();
  }

  private readonly dedupeKey = ({ job }: QueueJobArgs<AudioUploadJob>): string =>
    job.audioRecordingId;

  private readonly mergeOnDuplicate = ({
    existingJob,
    incomingJob,
  }: QueueMergeOnDuplicateArgs<AudioUploadJob>): void => {
    if (incomingJob.forceRestart) {
      existingJob.forceRestart = true;
    }
  };

  private readonly onQueueSuccess = ({
    job,
    result,
  }: QueueSuccessArgs<AudioUploadJob, UploadCompletionResult>): void => {
    if (!result.success) {
      const onUploadError = this.callbacks?.onUploadError;

      if (onUploadError) {
        onUploadError({
          job,
          error: result.error,
        });
      }

      return;
    }

    const onUploadComplete = this.callbacks?.onUploadComplete;

    if (onUploadComplete) {
      onUploadComplete({ job });
    }
  };

  private readonly onQueueError = ({ job, error }: QueueErrorArgs<AudioUploadJob>): void => {
    const onUploadError = this.callbacks?.onUploadError;

    if (onUploadError) {
      onUploadError({ job, error });
    }
  };

  private readonly toErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }

    return String(error);
  };

  private readonly setProgressSafely = ({
    forceRestart = false,
    progress,
  }: {
    forceRestart?: boolean;
    progress: AudioUploadProgressSetProgressArgs;
  }): void => {
    try {
      this.handlers.progress.setProgress(progress);
    } catch {
      this.emitDiagnosticEvent({
        type: AudioUploadDiagnosticEventType.ProgressSetFailed,
        audioRecordingId: progress.audioRecordingId,
        forceRestart,
      });
      // Progress updates are UI-facing only and should not change upload outcomes.
    }
  };

  private readonly clearProgressSafely = ({
    audioRecording,
    forceRestart = false,
  }: {
    audioRecording: AudioUploadProgressClearProgressArgs;
    forceRestart?: boolean;
  }): void => {
    try {
      this.handlers.progress.clearProgress(audioRecording);
    } catch {
      this.emitDiagnosticEvent({
        type: AudioUploadDiagnosticEventType.ProgressClearFailed,
        audioRecordingId: audioRecording.audioRecordingId,
        forceRestart,
      });
      // Progress cleanup is best-effort after the durable upload state is written.
    }
  };

  private readonly markAsUploadErroredSafely = async ({
    audioRecordingId,
    errorMessage,
    uploadStage,
    forceRestart = false,
    debugInfo,
  }: AudioUploadErrorRef & { forceRestart?: boolean }): Promise<void> => {
    try {
      await this.handlers.persistence.markAsUploadErrored({
        audioRecordingId,
        errorMessage,
        uploadStage,
        forceRestart,
        debugInfo,
      });
    } catch (error) {
      this.emitDiagnosticEvent({
        type: AudioUploadDiagnosticEventType.ErrorPersistenceFailed,
        audioRecordingId,
        forceRestart,
        errorMessage,
        persistenceErrorMessage: this.toErrorMessage(error),
      });
      // Preserve the triggering upload error even if persistence cleanup also fails.
    }
  };

  private readonly finishWithError = async ({
    audioRecordingId,
    errorMessage,
    uploadStage,
    shouldPersistError = true,
    forceRestart = false,
    debugInfo,
  }: AudioUploadErrorRef & {
    shouldPersistError?: boolean;
  }): Promise<QueueProcessResult<UploadCompletionResult>> => {
    this.emitDiagnosticEvent({
      type: AudioUploadDiagnosticEventType.FinishWithError,
      audioRecordingId,
      forceRestart,
      errorMessage,
      shouldPersistError,
    });

    if (shouldPersistError) {
      await this.markAsUploadErroredSafely({
        audioRecordingId,
        errorMessage,
        uploadStage,
        forceRestart,
        debugInfo,
      });
    }

    this.setProgressSafely({
      forceRestart,
      progress: {
        audioRecordingId,
        state: {
          uploadProgressPercent: null,
          isUploading: false,
          errorMessage,
        },
      },
    });

    return {
      success: true,
      result: {
        success: false,
        error: errorMessage,
      },
    };
  };

  private readonly processJob = async ({
    job: { audioRecordingId, forceRestart },
  }: QueueJobArgs<AudioUploadJob>): Promise<QueueProcessResult<UploadCompletionResult>> => {
    let prepared: PreparedAudioUploadResult<TSourceRef>;
    const shouldForceRestart = forceRestart ?? false;

    this.emitDiagnosticEvent({
      type: AudioUploadDiagnosticEventType.PrepareStarted,
      audioRecordingId,
      forceRestart: shouldForceRestart,
    });

    try {
      prepared = await this.handlers.payload.prepareUploadPayload({ audioRecordingId });
    } catch (error) {
      return this.finishWithError({
        audioRecordingId,
        errorMessage: this.toErrorMessage(error),
        uploadStage: AudioUploadFailureStage.PrepareFailed,
        forceRestart: shouldForceRestart,
      });
    }

    if (!prepared.success) {
      this.emitDiagnosticEvent({
        type: AudioUploadDiagnosticEventType.PrepareFailed,
        audioRecordingId,
        forceRestart: shouldForceRestart,
        errorMessage: prepared.error,
        shouldPersistError: prepared.shouldPersistError !== false,
      });
      return this.finishWithError({
        audioRecordingId,
        errorMessage: prepared.error,
        uploadStage: prepared.uploadStage ?? AudioUploadFailureStage.PrepareFailed,
        shouldPersistError: prepared.shouldPersistError !== false,
        forceRestart: shouldForceRestart,
        debugInfo: prepared.debugInfo,
      });
    }

    this.emitDiagnosticEvent({
      type: AudioUploadDiagnosticEventType.PrepareSucceeded,
      audioRecordingId,
      forceRestart: shouldForceRestart,
    });

    try {
      await this.handlers.persistence.markAsUploading({
        audioRecordingId,
        forceRestart: shouldForceRestart,
      });
    } catch (error) {
      this.emitDiagnosticEvent({
        type: AudioUploadDiagnosticEventType.MarkUploadingFailed,
        audioRecordingId,
        forceRestart: shouldForceRestart,
        errorMessage: this.toErrorMessage(error),
      });
      return this.finishWithError({
        audioRecordingId,
        errorMessage: this.toErrorMessage(error),
        uploadStage: AudioUploadFailureStage.MarkUploadingFailed,
        forceRestart: shouldForceRestart,
      });
    }

    this.setProgressSafely({
      forceRestart: shouldForceRestart,
      progress: {
        audioRecordingId,
        state: {
          uploadProgressPercent: 0,
          isUploading: true,
          errorMessage: null,
        },
      },
    });

    let uploadResult: UploadTransportResult = {
      success: false,
      error: "Upload failed",
    };

    this.emitDiagnosticEvent({
      type: AudioUploadDiagnosticEventType.TransportStarted,
      audioRecordingId,
      forceRestart: shouldForceRestart,
    });

    try {
      uploadResult = await this.handlers.transport.upload({
        source: prepared.payload.source,
        uploadMeta: prepared.payload.uploadMeta,
        forceRestart,
        onProgress: ({ progressPercent }) => {
          this.setProgressSafely({
            forceRestart: shouldForceRestart,
            progress: {
              audioRecordingId,
              state: {
                uploadProgressPercent: Math.round(progressPercent),
                isUploading: true,
                errorMessage: null,
              },
            },
          });
        },
      });
    } catch (error) {
      const errorMessage = this.toErrorMessage(error);
      uploadResult = {
        success: false,
        error: errorMessage,
      };
    }

    if (!uploadResult.success) {
      const errorMessage = uploadResult.error ?? "Upload failed";
      this.emitDiagnosticEvent({
        type: AudioUploadDiagnosticEventType.TransportFailed,
        audioRecordingId,
        forceRestart: shouldForceRestart,
        errorMessage,
      });
      return this.finishWithError({
        audioRecordingId,
        errorMessage,
        uploadStage: AudioUploadFailureStage.TransportFailed,
        forceRestart: shouldForceRestart,
      });
    }

    this.emitDiagnosticEvent({
      type: AudioUploadDiagnosticEventType.TransportSucceeded,
      audioRecordingId,
      forceRestart: shouldForceRestart,
    });

    try {
      await this.handlers.persistence.markAsUploadCompleted({ audioRecordingId });
    } catch (error) {
      const errorMessage = this.toErrorMessage(error);
      this.emitDiagnosticEvent({
        type: AudioUploadDiagnosticEventType.MarkCompletedFailed,
        audioRecordingId,
        forceRestart: shouldForceRestart,
        errorMessage,
      });

      return this.finishWithError({
        audioRecordingId,
        errorMessage,
        uploadStage: AudioUploadFailureStage.MarkCompletedFailed,
        forceRestart: shouldForceRestart,
      });
    }

    this.clearProgressSafely({
      audioRecording: { audioRecordingId },
      forceRestart: shouldForceRestart,
    });

    return {
      success: true,
      result: {
        success: true,
      },
    };
  };

  private readonly emitDiagnosticEvent = (event: AudioUploadDiagnosticEvent): void => {
    try {
      this.diagnostics?.onEvent?.({ event });
    } catch {
      // Diagnostics are best-effort and must not change upload behavior.
    }
  };
}
